import { MutableRefObject, RefObject, useEffect, useMemo, useRef, useState } from "react";
import PageLayout from "../components/PageLayout";
import { AIRFRAME_PHOTOS } from "../constants/airframePhotos";
import { cardImage } from "../constants/photos";
import { usePublicData } from "../hooks/usePublicData";
import usePilotName from "../hooks/usePilotName";
import usePilotProficiencyProgress, { ProgressByGroup } from "../hooks/usePilotProficiencyProgress";
import { useLens } from "../lens/LensProvider";
import { fetchProficienciesCatalogue } from "../services/proficienciesService";
import { CatalogueAirframe, ProficiencyDefinition, ProficiencyLevel } from "../types/proficiencies";
import { formatPilotCount } from "../utils/pilotCount";
import { shortTierLabel, stripAirframePrefix } from "../utils/proficiencyName";
import { renderSafeDescription } from "../utils/safeDescription";

const sectionId = (airframeId: string) => `prof-${airframeId}`;
const ACTIVE_OFFSET_PX = 120;

function scrollToSection(airframeId: string, smooth: boolean) {
  const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  document.getElementById(sectionId(airframeId))?.scrollIntoView({ behavior: smooth && !reduceMotion ? "smooth" : "auto", block: "start" });
}

function photoStyle(airframeId: string, size: "thumb" | "tile") {
  const key = AIRFRAME_PHOTOS[airframeId];
  return key ? { backgroundImage: cardImage(key, size) } : undefined;
}

function tierColumns(airframe: CatalogueAirframe) {
  const columns = new Map<string, string>();
  airframe.proficiencies.forEach(p => p.levels.forEach(level => { if (!columns.has(level.code)) columns.set(level.code, level.label); }));
  return Array.from(columns, ([code, label]) => ({ code, label }));
}

function proficiencyCount(airframe: CatalogueAirframe) {
  const n = airframe.proficiencies.length;
  return n === 1 ? "1 proficiency" : `${n} proficiencies`;
}

type ProgressLookup = Map<string, ProficiencyLevel[]>;

// Progress has no stable proficiency ID: join its documented group and name to the catalogue.
const progressKey = (group: string, displayName: string) => `${group}|${displayName}`;

function buildLookup(progress: ProgressByGroup | null | undefined): ProgressLookup {
  const lookup: ProgressLookup = new Map();
  Object.values(progress ?? {}).flat().forEach(p => {
    const key = progressKey(p.group, p.display_name);
    if (!lookup.has(key)) lookup.set(key, p.levels);
  });
  return lookup;
}

const plainText = (html: string) => html.replace(/<[^>]*>/g, "").trim();

function levelsFor(lookup: ProgressLookup, p: ProficiencyDefinition) {
  return lookup.get(progressKey(p.group, p.display_name));
}

// Tiers cleared across every proficiency in the airframe, as a share of all its tiers.
function percentComplete(airframe: CatalogueAirframe, lookup: ProgressLookup): number {
  const total = airframe.proficiencies.reduce((n, p) => n + p.levels.length, 0);
  if (!total) return 0;
  const cleared = airframe.proficiencies.reduce((n, p) => n + (levelsFor(lookup, p)?.filter(l => l.complete).length ?? 0), 0);
  return Math.round((cleared / total) * 100);
}

function hasProgress(airframe: CatalogueAirframe, lookup: ProgressLookup) {
  return airframe.proficiencies.some(p => levelsFor(lookup, p)?.some(l => l.status !== "none"));
}

function AirframeTile({ airframe, onJump, href, overlay }: {
  airframe: CatalogueAirframe; onJump: (id: string) => void; href: string; overlay: ProgressLookup | null;
}) {
  if (!airframe.proficiencies.length) {
    return <div className="airframe-tile is-pending" aria-label={`${airframe.display_name}, coming soon`}>
      <div className="airframe-tile-photo" style={photoStyle(airframe.id, "tile")} />
      <div className="airframe-tile-body"><h3>{airframe.display_name}</h3><span className="airframe-tile-count">Coming soon</span></div>
    </div>;
  }
  const complete = overlay ? percentComplete(airframe, overlay) : 0;
  return <a className="airframe-tile" href={href}
    onClick={e => { e.preventDefault(); onJump(airframe.id); }}>
    <div className="airframe-tile-photo" style={photoStyle(airframe.id, "tile")} />
    <div className="airframe-tile-body">
      <h3>{airframe.display_name}</h3>
      <span className="airframe-tile-count">{proficiencyCount(airframe)}</span>
      {complete > 0 && <span className="airframe-tile-complete">{complete}% complete</span>}
    </div>
  </a>;
}

function TierCell({ level, unit, label, pilotLevels, showCommunity }: {
  level: ProficiencyDefinition["levels"][number] | undefined; unit: string; label: string;
  pilotLevels: ProficiencyLevel[] | undefined; showCommunity: boolean;
}) {
  if (!level) return <td data-label={label}>—</td>;
  const mine = pilotLevels?.find(l => l.code === level.code);
  const nextCode = pilotLevels?.find(l => !l.complete)?.code;
  const reached = mine?.complete === true;
  const started = !!pilotLevels?.some(l => l.status !== "none");
  const isNext = !!mine && !reached && started && nextCode === level.code;
  const unitLabel = level.at === 1 ? unit.replace(/s$/, "") : unit;
  const requirement = `${level.at.toLocaleString()} ${unitLabel}`;
  const cumulative = mine ? Math.min(mine.from + mine.progress, level.at) : 0;
  const fill = isNext && mine && mine.required > 0 ? Math.min(100, Math.max(0, (mine.progress / mine.required) * 100)) : 0;
  const state = showCommunity ? "is-plain" : reached ? "is-reached" : isNext ? "is-next" : "is-ahead";
  return <td data-label={label}>
    <span className={`tier-chip ${state}`} style={isNext ? { ["--p" as string]: `${fill}%` } : undefined}>
      {reached && <span className="tier-chip-sr">Reached: </span>}
      <span>{isNext ? `${cumulative.toLocaleString()} / ${requirement}` : requirement}</span>
      {showCommunity && !!level.reached_count && <span className="prof-reached">{formatPilotCount(level.reached_count)}</span>}
    </span>
  </td>;
}

function AirframeSection({ airframe, overlay, topHref, onTop }: {
  airframe: CatalogueAirframe; overlay: ProgressLookup | null; topHref: string; onTop: () => void;
}) {
  const columns = tierColumns(airframe);
  return <section className="prof-section" id={sectionId(airframe.id)}>
    <div className="airframe-banner">
      <div className="airframe-banner-photo" style={photoStyle(airframe.id, "thumb")} />
      <div className="airframe-banner-title">
        <h2>{airframe.display_name}</h2>
        <span className="airframe-tile-count">{proficiencyCount(airframe)}</span>
      </div>
      <a href={topHref} onClick={e => { e.preventDefault(); onTop(); }}>All airframes ↑</a>
    </div>
    <div className="prof-body">
      {!airframe.proficiencies.length ? <p className="dashboard-empty-row">No proficiencies are defined for this airframe yet.</p> :
        <table className="prof-table">
          <thead><tr><th scope="col">Proficiency</th>{columns.map(c => <th scope="col" key={c.code} title={c.label}>{shortTierLabel(c.label)}</th>)}</tr></thead>
          <tbody>{airframe.proficiencies.map(p => {
            const pilotLevels = overlay ? levelsFor(overlay, p) : undefined;
            return <tr key={p.id}>
              <th scope="row" title={p.description ? plainText(p.description) : undefined}>
                <span className="prof-name">{stripAirframePrefix(p.display_name, airframe.display_name)}</span>
                {p.description && <span className="prof-desc">{renderSafeDescription(p.description)}</span>}</th>
              {columns.map(c => <TierCell key={c.code} level={p.levels.find(l => l.code === c.code)} unit={p.unit}
                label={shortTierLabel(c.label)} pilotLevels={pilotLevels} showCommunity={!overlay} />)}
            </tr>;
          })}</tbody>
        </table>}
    </div>
  </section>;
}

function useScrollState(ids: string[], gridRef: RefObject<HTMLElement>, pinned: MutableRefObject<string | null>) {
  const [state, setState] = useState<{ active: string | null; pastGrid: boolean }>({ active: null, pastGrid: false });
  const key = ids.join("|");
  useEffect(() => {
    if (!ids.length) return;
    const update = () => {
      let active: string | null = null;
      for (const id of ids) {
        const el = document.getElementById(sectionId(id));
        if (el && el.getBoundingClientRect().top <= ACTIVE_OFFSET_PX) active = id;
      }
      if (pinned.current) {
        const top = document.getElementById(sectionId(pinned.current))?.getBoundingClientRect().top;
        if (top !== undefined && top > -ACTIVE_OFFSET_PX && top < window.innerHeight * 0.7) active = pinned.current;
        else pinned.current = null;
      }
      const pastGrid = !!gridRef.current && gridRef.current.getBoundingClientRect().bottom < 0;
      setState(prev => (prev.active === active && prev.pastGrid === pastGrid ? prev : { active, pastGrid }));
    };
    update();
    window.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
    };
  }, [key]);
  return state;
}

export default function ProficienciesPage({ airframe }: { airframe: string | null }) {
  const { activeUid, viewUid, setScope } = useLens();
  const name = usePilotName(activeUid);
  const { data, error, loading, refresh } = usePublicData("catalogue", fetchProficienciesCatalogue);
  const progress = usePilotProficiencyProgress(activeUid);
  const overlay = useMemo(() => (activeUid && progress.data ? buildLookup(progress.data) : null), [activeUid, progress.data]);
  const catalogue = data?.airframes ?? [];
  const byName = (a: CatalogueAirframe, b: CatalogueAirframe) => a.display_name.localeCompare(b.display_name);
  const allAirframes = catalogue.filter(a => a.proficiencies.length > 0).sort(byName);
  // The pilot lens shows only airframes the pilot has progress in; Everyone is the full catalogue.
  const airframes = overlay ? allAirframes.filter(a => hasProgress(a, overlay)) : allAirframes;
  const pending = overlay ? [] : catalogue.filter(a => !a.proficiencies.length).sort(byName);
  const awaitingProgress = !!activeUid && progress.loading;
  // Not resolved until the sections are on screen, so the scroll effect fires when its target exists.
  const requested = airframe && !awaitingProgress ? airframes.find(a => a.id === airframe) : undefined;
  const hiddenRequested = airframe && !requested && overlay ? allAirframes.find(a => a.id === airframe) : undefined;
  const gridRef = useRef<HTMLElement>(null);
  const pinned = useRef<string | null>(null);
  const { active, pastGrid } = useScrollState(airframes.map(a => a.id), gridRef, pinned);
  const showAllAirframes = viewUid
    ? <a href="#/proficiencies">Show all airframes</a>
    : <button type="button" onClick={() => setScope("everyone")}>Show all airframes</button>;

  const airframeHash = (id: string) => {
    const params = new URLSearchParams();
    if (viewUid) params.set("pilot", viewUid);
    params.set("airframe", id);
    return `#/proficiencies?${params.toString()}`;
  };

  useEffect(() => {
    if (!requested) return;
    pinned.current = requested.id;
    const frame = requestAnimationFrame(() => scrollToSection(requested.id, false));
    return () => cancelAnimationFrame(frame);
  }, [requested?.id]);

  const jump = (id: string) => {
    window.history.replaceState(null, "", airframeHash(id));
    pinned.current = id;
    scrollToSection(id, true);
  };

  const listHash = viewUid ? `#/proficiencies?pilot=${encodeURIComponent(viewUid)}` : "#/proficiencies";
  const toTop = () => {
    window.history.replaceState(null, "", listHash);
    pinned.current = null;
    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    window.scrollTo({ top: 0, behavior: reduceMotion ? "auto" : "smooth" });
  };

  return <PageLayout route="proficiencies" hero={{
    photo: "cockpit", focus: "50% 59%", align: "end", title: "Proficiencies",
    body: "Checkride tallies every sortie towards a proficiency, from Basic to Master.",
  }}>
    {loading && <p role="status">Loading proficiency catalogue…</p>}
    {error && <div className="alert" role="alert">Unable to load the catalogue. <button onClick={refresh}>Retry</button></div>}
    {data && <>
      {awaitingProgress && <p role="status" className="prof-legend">Loading {name}'s progress…</p>}
      {activeUid && progress.error && <div className="alert" role="alert">Progress is unavailable. <button onClick={progress.refresh}>Retry</button></div>}
      {!awaitingProgress && <>
      {!catalogue.length && <p>No proficiency definitions are available yet.</p>}
      {airframe && !requested && !hiddenRequested && <p className="dashboard-empty-row">This airframe is unavailable. Choose another airframe.</p>}
      {hiddenRequested && <p className="dashboard-empty-row">{name} has no progress in {hiddenRequested.display_name} yet. {showAllAirframes}</p>}
      {overlay && !airframes.length && <p className="dashboard-empty-row">{name} has no proficiency progress yet. {showAllAirframes}</p>}
      {airframes.length + pending.length > 0 && <section className="airframe-grid" id="prof-airframes" ref={gridRef} aria-label="Airframes">
        {[...airframes, ...pending].map(a => <AirframeTile key={a.id} airframe={a} onJump={jump} href={airframeHash(a.id)} overlay={overlay} />)}
      </section>}
      {airframes.length > 1 && <div className="prof-jump-wrap">
        <nav className={"prof-jump" + (pastGrid ? " is-visible" : "")} aria-label="Jump to airframe" aria-hidden={!pastGrid}>
          {airframes.map(a => <a key={a.id} href={airframeHash(a.id)} tabIndex={pastGrid ? 0 : -1}
            className={a.id === active ? "is-active" : undefined} aria-current={a.id === active ? "location" : undefined}
            onClick={e => { e.preventDefault(); jump(a.id); }}>{a.display_name}</a>)}
        </nav>
      </div>}
      {airframes.map(a => <AirframeSection key={a.id} airframe={a} overlay={overlay} topHref={listHash} onTop={toTop} />)}
      </>}
    </>}
  </PageLayout>;
}
